

import React, { useEffect, useState } from 'react'
import QRCode from 'qrcode';
import Loader from '../../components/loader';
import Layout from '../../components/Layout';
import api from "../../utils/api";
import { errorMessage } from '../../utils/utils';
import { useParams, useSearchParams } from 'react-router-dom';
import moment from 'moment';
import maya from '../../assets/paymaya-logo.svg';
import gcash from '../../assets/gcash-logo.svg';
import grab from '../../assets/grabpay-logo.svg';
import shopee from '../../assets/shopeepay-logo-2.svg';
import qrph from '../../assets/qrph-logo.svg';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import { FaCheck, FaExclamation, FaExternalLinkSquareAlt, FaQuestion } from 'react-icons/fa';
import { FcMoneyTransfer } from 'react-icons/fc';
import { ErrorState } from '../../components/ui';
import { ListLoading } from '../../components/ListToolbar';
import { peso, signedPeso } from '../../components/Credits';

export default function TransactionItem() {

    const { id } = useParams();
    const [searchParams] = useSearchParams();

    const [pageLoading, setPageLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [data, setData] = useState<any>();
    const [payment, setPayment] = useState<any>();
    // QR Ph: the code to scan, drawn from `qr_string`.
    const [qrImage, setQrImage] = useState('');


    let intervalCallback: any;
    let isOpenDialog = false;

    useEffect(() => {
        const token = window.localStorage.token;

        if (!token) {
            window.location.replace('/signin');
            return;
        }

        setPageLoading(false);
        getData();

    }, []);

    const waitingQr = data?.status === 'WAITING' && data?.payment_method === 'qrph' && !!data?.qr_string;

    useEffect(() => {
        if (!waitingQr) {
            setQrImage('');
            return;
        }
        QRCode.toDataURL(data.qr_string, { margin: 1, errorCorrectionLevel: 'M', width: 600, color: { dark: '#0f172a', light: '#ffffff' } })
            .then(setQrImage)
            .catch(() => setQrImage(''));
    }, [waitingQr, data?.qr_string]);

    // A QR Ph payment has no checkout tab to come back from: while the code shows, ask every few
    // seconds (the API asks Xendit, kptd-remote-api credits/qrph.ts) and reload once it is settled.
    useEffect(() => {
        if (!waitingQr) return;
        let stopped = false;
        const timer = setInterval(async () => {
            if (document.hidden) return;
            try {
                const res = await api.get(`/remote/payment/status/${id}`);
                const json = await res.json();
                if (!stopped && res.ok && json.status !== 'WAITING') {
                    stopped = true;
                    clearInterval(timer);
                    getData();
                }
            } catch (err) { }
        }, 4000);
        return () => {
            stopped = true;
            clearInterval(timer);
        };
        // getData is a new function every render but only reads `id` and setters, so the first one is enough.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [waitingQr, id]);

    if (pageLoading) {
        return (<div className='w-screen h-screen text-center flex flex-col bg-white justify-center'><Loader /></div>)
    }

    async function getData() {
        setLoading(true);
        try {
            const res = await api.get(`/remote/transaction/${id}`);
            const json = await res.json();
            if (res.ok) {
                setData(json);
                if (searchParams.get('u') === '1') {
                    checkPayment(json);
                }
            } else {
                setError(errorMessage(json));
            }
        } catch (err) {
            setError(errorMessage(err));
        }
        setLoading(false);
    }

    async function checkPayment(json: any) {
        // QR Ph is scanned on this page; there is no checkout tab to open.
        if (json?.status !== 'WAITING' || json?.payment_method === 'qrph') return;

        setTimeout(() => {
            setPayment({
                url: json?.payment_url,
                status: json?.status?.toLowerCase(),
                message: `Please proceed with the payment. A new tab will open for this process. If it doesn’t, copy and paste the following URL into a new tab.`,
            });
            setOpenDialog(true);
            isOpenDialog = true;
        }, 1000);

        setTimeout(() => window.open(json?.payment_url, '_blank'), 2000);

        setTimeout(() => {
            intervalCallback = setInterval(() => getPaymentStatus(json?.id, json?.action), 5000);
        }, 10000);
    }

    async function getPaymentStatus(id?: string, action?: string) {
        if (!id) return;

        try {

            const res = await api.get(`/remote/payment/status/${id}`);
            const json = await res.json();

            if (res.ok) {
                if (json.status !== 'WAITING') {
                    clearInterval(intervalCallback);

                    if (isOpenDialog) {
                        if (json.status === 'SUCCEEDED' || json.status === 'PENDING') {
                            setPayment({
                                status: 'success',
                                message: action === 'topup'
                                    ? 'Success! Your payment has been received and the credits are added to your balance. You can now use them to buy or renew remote accounts.'
                                    : 'Success! Your payment has been received. You can view your purchase or renewal on the Remote Accounts page. It may take a minute for your remote account to appear.'
                            });
                        } else {
                            setPayment({
                                status: 'failed',
                                message: 'Payment Failed! Please review your payment details for more information.'
                            });
                        }

                    } else {
                        window.location.replace(`/transaction/${id}`);
                    }
                }

            } else {
                clearInterval(intervalCallback);
                setPayment({
                    status: 'failed',
                    message: 'Error: Server error.'
                });
            }

        } catch (error) {
            console.trace('get payment error', error);
            clearInterval(intervalCallback);
            setPayment({
                status: 'failed',
                message: 'Error: Server error.'
            });
        }
    }

    function getTransactionDate() {
        if (!data || !data?.date_created) return '';

        try {
            return moment(data.date_created).format('MMM DD YYYY hh:mm:ss a');
        } catch (error) {
            console.log('Error', error);
        }

        return '';
    }

    function statusBg(status?: string) {
        switch (status) {
            case 'SUCCEEDED': return 'bg-emerald-50 text-emerald-600';
            case 'PENDING': return 'bg-amber-50 text-amber-600';
            case 'ERROR':
            case 'FAILED': return 'bg-red-50 text-red-600';
            case 'REFUNDED': return 'bg-blue-50 text-blue-600';
            default: return 'bg-slate-100 text-slate-500';
        }
    }

    function statusDot(status?: string) {
        switch (status) {
            case 'SUCCEEDED': return 'bg-emerald-500';
            case 'PENDING': return 'bg-amber-500';
            case 'ERROR':
            case 'FAILED': return 'bg-red-500';
            case 'REFUNDED': return 'bg-blue-500';
            default: return 'bg-slate-300';
        }
    }

    function getPayment() {

        let payment: any;

        switch (data?.payment_method) {
            case 'qrph': payment = qrph;
                break;
            case 'maya': payment = maya;
                break;
            case 'gcash': payment = gcash;
                break;
            case 'shopeepay': payment = shopee;
                break;
            case 'grabpay': payment = grab;
                break;
        }

        return payment;
    }


    const isTopup = data?.action === 'topup';
    const isAdjustment = data?.action === 'adjustment';

    return (
        <>
            <Layout nav='transaction' title={`Transaction List > ${data?.id ?? ''}`}>
                <div className="mx-auto max-w-3xl px-5 py-8 sm:px-8">

                    <button
                        type="button"
                        onClick={() => window.history.back()}
                        className="inline-flex items-center gap-1 text-sm font-semibold text-slate-400 transition hover:text-slate-950">
                        <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
                            <path fillRule="evenodd" d="M17 10a.75.75 0 0 1-.75.75H5.612l4.158 3.96a.75.75 0 1 1-1.04 1.08l-5.5-5.25a.75.75 0 0 1 0-1.08l5.5-5.25a.75.75 0 1 1 1.04 1.08L5.612 9.25H16.25A.75.75 0 0 1 17 10Z" clipRule="evenodd" />
                        </svg>
                        Go back
                    </button>

                    <h1 className="mt-4 text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">Transaction Details</h1>

                    {loading && <ListLoading label="Loading transaction..." />}
                    {(!loading && error) && <ErrorState error={error} />}

                    {(!loading && !error && data) && <>

                        {/* Status banner */}
                        {data?.status === 'SUCCEEDED' && (
                            <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700">
                                <p className="font-semibold">{isAdjustment ? 'Credits adjusted' : isTopup ? 'Top-up successful' : 'Payment successful'}</p>
                                <p className="mt-1">{isAdjustment
                                    ? `${peso(Math.abs(data?.credits ?? 0))} credits were ${(data?.credits ?? 0) < 0 ? 'deducted from' : 'added to'} your balance by KPTD support.`
                                    : isTopup ? `\u20b1${(data?.credits ?? 0).toLocaleString()} credits were added to your balance.` : 'Your remote access has been activated.'}</p>
                            </div>
                        )}

                        {data?.status === 'REFUNDED' && (
                            <div className="mt-4 rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-700">
                                <p className="font-semibold">Credits returned</p>
                                <p className="mt-1">We could not complete this {data?.action === 'renew' ? 'renewal' : 'purchase'}, so the &#8369;{data?.payment?.total ?? 0} credits it used were returned to your balance.</p>
                            </div>
                        )}

                        {(data?.status === 'FAILED' || data?.status === 'ERROR') && (
                            <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                                <p className="font-semibold">Payment failed</p>
                                <p className="mt-1">{data?.failure ?? 'Your payment was not completed. You can try again.'}</p>
                                <a href={isTopup ? '/credits' : '/remote/new'} className="mt-3 inline-block rounded-full bg-red-600 px-4 py-2 text-xs font-bold text-white transition hover:bg-red-700">
                                    Try Again
                                </a>
                            </div>
                        )}

                        <div className="mt-6 space-y-5">

                            {/* QR Ph: scan to pay */}
                            {waitingQr && qrImage && (
                                <div className="rounded-2xl border border-slate-200 bg-white">
                                    <div className="flex flex-col items-center px-6 py-7 text-center">
                                        <img src={qrph} alt="QR Ph" className="h-8 w-auto" />
                                        <h2 className="mt-4 text-lg font-semibold text-slate-900">Scan to pay &#8369;{data?.payment?.total ?? ''}</h2>
                                        <p className="mt-1 text-xs text-slate-500">Use any Philippine bank or e-wallet app that supports QR Ph.</p>
                                        <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-4">
                                            <img src={qrImage} alt="QR Ph payment code" width={600} height={600} className="h-64 w-64 sm:h-80 sm:w-80" />
                                        </div>
                                        <a href={qrImage} download={`kptd-qrph-${data?.id}.png`} className="mt-3 text-xs font-semibold text-emerald-600 hover:underline">
                                            Save the QR code
                                        </a>
                                        <ol className="mt-5 w-full max-w-[18rem] space-y-1.5 text-left text-xs leading-6 text-slate-500">
                                            <li><span className="font-semibold text-slate-900">1.</span> Open your bank or e-wallet app.</li>
                                            <li><span className="font-semibold text-slate-900">2.</span> Tap Scan or Pay with QR. On this phone, save the code and upload it from your gallery.</li>
                                            <li><span className="font-semibold text-slate-900">3.</span> Confirm the payment.</li>
                                        </ol>
                                        <div className="mt-4 flex items-center gap-2 text-xs text-slate-500">
                                            <svg className="h-3.5 w-3.5 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                            </svg>
                                            Waiting for your payment&hellip;
                                        </div>
                                        <p className="mt-2 text-[11px] text-slate-400">
                                            This code can be paid until {moment(data?.date_created).add(30, 'minutes').format('h:mm a')}. Your credits are added as soon as the payment goes through, even if you close this page.
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* Complete payment */}
                            {data?.payment_url && (
                                <a
                                    href={data?.payment_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="flex items-center justify-center gap-2 rounded-2xl border border-blue-200 bg-blue-50 px-6 py-4 text-sm font-bold text-blue-700 transition hover:bg-blue-100">
                                    {getPayment() && <img src={getPayment()} alt={data?.payment_method} className="h-5 max-w-[60px] object-contain" />}
                                    Complete Payment &mdash; &#8369; {data?.payment?.total ?? ''}
                                    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
                                        <path fillRule="evenodd" d="M3 10a.75.75 0 0 1 .75-.75h10.638l-3.96-4.158a.75.75 0 1 1 1.08-1.04l5.25 5.5a.75.75 0 0 1 0 1.08l-5.25 5.5a.75.75 0 1 1-1.08-1.04l3.96-4.158H3.75A.75.75 0 0 1 3 10Z" clipRule="evenodd" />
                                    </svg>
                                </a>
                            )}

                            {/* Payment */}
                            <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6">
                                <div className="flex items-center justify-between gap-2">
                                    <div className="flex items-center gap-2">
                                        <h2 className="text-sm font-bold uppercase tracking-wider text-slate-400">Payment</h2>
                                        <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                                            {data?.action}
                                        </span>
                                    </div>
                                    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-widest ${statusBg(data?.status)}`}>
                                        <span className={`h-1.5 w-1.5 rounded-full ${statusDot(data?.status)}`} />
                                        {data?.status?.toLowerCase()}
                                    </span>
                                </div>

                                <div className="mt-5 flex items-baseline gap-2">
                                    <span className="text-3xl font-bold text-emerald-600">{isAdjustment ? signedPeso(data?.credits) : <>&#8369; {data?.payment?.total ?? '0'}</>}</span>
                                </div>

                                <div className="mt-5 grid gap-4 sm:grid-cols-2">
                                    <div>
                                        <p className="text-xs font-semibold text-slate-400">Payment Method</p>
                                        <div className="mt-1.5 flex items-center gap-2">
                                            {getPayment() && <img src={getPayment()} alt={data?.payment_method} className="h-5 max-w-[60px] object-contain" />}
                                            <p className="text-sm font-medium text-slate-800">{data?.payment_method ?? '\u2014'}</p>
                                        </div>
                                    </div>
                                    <div>
                                        <p className="text-xs font-semibold text-slate-400">Date</p>
                                        <p className="mt-1.5 text-sm font-medium text-slate-800">{getTransactionDate()}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs font-semibold text-slate-400">Subtotal</p>
                                        <p className="mt-1.5 text-sm font-medium text-slate-800">{data?.payment?.subtotal ?? ''}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs font-semibold text-slate-400">Fee</p>
                                        <p className="mt-1.5 text-sm font-medium text-slate-800">{data?.payment?.fee ?? ''}</p>
                                    </div>
                                    {data?.balance !== undefined && (
                                        <div className="sm:col-span-2">
                                            <p className="text-xs font-semibold text-slate-400">Credits balance after</p>
                                            <p className="mt-1.5 text-sm font-medium text-slate-800">&#8369;{data.balance.toLocaleString()}</p>
                                        </div>
                                    )}
                                    <div className="sm:col-span-2">
                                        <p className="text-xs font-semibold text-slate-400">Reference</p>
                                        <p className="mt-1.5 break-all font-mono text-xs text-slate-500">{data?.id ?? ''}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Remote / order details */}
                            {isAdjustment ? (
                                <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6">
                                    <h2 className="text-sm font-bold uppercase tracking-wider text-slate-400">Adjustment Details</h2>
                                    <div className="mt-4 grid gap-4 sm:grid-cols-2">
                                        <div>
                                            <p className="text-xs font-semibold text-slate-400">Credits</p>
                                            <p className="mt-1 text-sm font-medium text-slate-800">{signedPeso(data?.credits)}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs font-semibold text-slate-400">Made by</p>
                                            <p className="mt-1 text-sm font-medium text-slate-800">KPTD support</p>
                                        </div>
                                        <div className="sm:col-span-2">
                                            <p className="text-xs font-semibold text-slate-400">Reason</p>
                                            <p className="mt-1 break-words text-sm font-medium text-slate-800">{data?.data?.reason ?? '\u2014'}</p>
                                        </div>
                                    </div>
                                </div>
                            ) : isTopup ? (
                                <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6">
                                    <h2 className="text-sm font-bold uppercase tracking-wider text-slate-400">Top-up Details</h2>
                                    <div className="mt-4 grid gap-4 sm:grid-cols-2">
                                        <div>
                                            <p className="text-xs font-semibold text-slate-400">Credits</p>
                                            <p className="mt-1 text-sm font-medium text-slate-800">&#8369;{(data?.credits ?? 0).toLocaleString()}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs font-semibold text-slate-400">Use them</p>
                                            <a className="mt-1 block text-sm font-medium text-emerald-600 hover:underline" href="/credits">View your credits</a>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                            <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6">
                                <h2 className="text-sm font-bold uppercase tracking-wider text-slate-400">
                                    {data?.action?.toLowerCase() === 'renew' ? 'Renewal Details' : 'Remote Details'}
                                </h2>

                                <div className="mt-4 grid gap-4 sm:grid-cols-2">

                                    {data?.action?.toLowerCase() === 'renew' ? <>
                                        <div>
                                            <p className="text-xs font-semibold text-slate-400">Duration</p>
                                            <p className="mt-1 text-sm font-medium text-slate-800">{data?.quantity} {data?.unit}{parseInt(data?.quantity) > 1 && 's'}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs font-semibold text-slate-400">Remote</p>
                                            <a className="mt-1 block break-all text-sm font-medium text-emerald-600 hover:underline" href={`/remote/${data?.data?.uid}`}>
                                                {data?.data?.uid ?? ''}
                                            </a>
                                        </div>
                                    </> : <>
                                        <div>
                                            <p className="text-xs font-semibold text-slate-400">Name</p>
                                            <p className="mt-1 text-sm font-medium text-slate-800">{data?.data?.label ?? ''}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs font-semibold text-slate-400">Server</p>
                                            <p className="mt-1 text-sm font-medium text-slate-800">{data?.data?.server ?? ''}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs font-semibold text-slate-400">VPN</p>
                                            <p className="mt-1 text-sm font-medium uppercase text-slate-800">{data?.data?.service ?? ''}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs font-semibold text-slate-400">Port</p>
                                            <p className="mt-1 text-sm font-medium text-slate-800">
                                                {data?.data?.app_port === 'custom'
                                                    ? `${data?.data?.protocol ?? ''}:${data?.data?.custom ?? ''}`
                                                    : data?.data?.app_port ?? ''}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-xs font-semibold text-slate-400">Plan</p>
                                            <p className="mt-1 text-sm font-medium text-slate-800">{data?.quantity} {data?.unit}{parseInt(data?.quantity) > 1 && 's'}</p>
                                        </div>
                                    </>}

                                </div>
                            </div>
                            )}

                        </div>

                    </>}

                </div>
            </Layout>



            <Dialog open={openDialog} onClose={() => {
                isOpenDialog = false;
            }} className="relative z-[60]">
                <DialogBackdrop
                    transition
                    className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
                />

                <div className="fixed inset-0 z-[60] w-screen overflow-y-auto">
                    <div className="flex min-h-full justify-center p-4 text-center items-center">
                        <DialogPanel
                            transition
                            className="w-full sm:w-[450px] py-8 px-5 sm:px-10 relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
                        >

                            <div className={`mx-auto flex h-[100px] w-[100px] flex-shrink-0 items-center justify-center rounded-full ${payment?.status === 'waiting' ? 'bg-blue-50' : payment?.status === 'success' ? 'bg-green-200' : payment?.status === 'failed' ? 'bg-red-200' : 'bg-gray-200'}`}>
                                {!payment?.status && <FaQuestion className="h-[50px] w-[50px] text-gray-500" />}
                                {payment?.status === 'waiting' && <FcMoneyTransfer className="h-[70px] w-[70px]" />}
                                {payment?.status === 'failed' && <FaExclamation className="h-[50px] w-[50px] text-red-500" />}
                                {payment?.status === 'success' && <FaCheck className="h-[50px] w-[50px] text-green-500" />}
                            </div>

                            <div className={`text-center mt-5 ${!payment?.amount && 'hidden'}`}>
                                <div className='text-sm'>Amount to be paid:</div>
                                <div className='text-2xl font-medium'>₱{payment?.amount ?? '0'}</div>
                            </div>

                            <div className="my-5 text-sm text-center text-gray-500 w-full"><div dangerouslySetInnerHTML={{ __html: payment?.message }} /></div>

                            <div className={`text-center mb-5 ${!payment?.url && 'hidden'}`}><a href={payment?.url} target='_blank' rel="noreferrer" className='text-xs text-blue-700 break-all'>{payment?.url}</a></div>

                            <div className='text-center italic text-xs text-gray-400'>You can safely close this dialog and check the status of your payment.</div>

                            <div className="text-center mt-5">
                                <button
                                    type="button"
                                    data-autofocus
                                    onClick={() => {
                                        clearInterval(intervalCallback);
                                        if (payment.status !== 'waiting') {
                                            window.location.replace(`/transaction/${id}`);
                                        } else {
                                            isOpenDialog = false;
                                            setOpenDialog(false);
                                        }
                                    }}
                                    className="inline-flex justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                                >Close</button>
                            </div>

                        </DialogPanel>
                    </div>
                </div>
            </Dialog>
        </>
    )
}
